import type { TrackingIdentifier } from '@ael/core-api';
import type {
  OpenQuestionAnswerEntry,
  OpenQuestionAnswersForItems,
} from '../../../../reducers/student/tracking/openQuestionAnswersReducer';
import { InteractionId, InteractionResponse } from './InteractionCodecs';
import type { ScormClient } from './scormTyped';
import { Map } from 'immutable';

export const storeInteractionsToLms2004 = (answers: OpenQuestionAnswersForItems, scormClient: ScormClient): void => {
  if (answers.isEmpty()) return;

  try {
    const countStr = scormClient.get('cmi.interactions._count');
    let nextIndex = parseInt(countStr, 10) || 0;

    const existingInteractions = new globalThis.Map<string, number>();
    for (let i = 0; i < nextIndex; i++) {
      const id = scormClient.get(`cmi.interactions.${i}.id`);
      existingInteractions.set(id, i);
    }

    answers.forEach((widgetAnswers, contentItemTrackingId) => {
      widgetAnswers.forEach((entry, widgetTrackingId) => {
        const interactionId = InteractionId.encode(contentItemTrackingId, widgetTrackingId);
        let index = existingInteractions.get(interactionId);

        if (index === undefined) {
          index = nextIndex;
          nextIndex++;
          existingInteractions.set(interactionId, index);
        }

        const formattedResponse = InteractionResponse.encode(entry);
        scormClient.set(`cmi.interactions.${index}.id`, interactionId);
        scormClient.set(`cmi.interactions.${index}.type`, 'fill-in');
        scormClient.set(`cmi.interactions.${index}.learner_response`, formattedResponse);
      });
    });
  } catch (error) {
    console.error('Error storing interactions to LMS 2004:', error);
  }
};

export const getInteractionsFromLms2004 = (scormClient: ScormClient): OpenQuestionAnswersForItems | null => {
  const mutableMap = new globalThis.Map<
    TrackingIdentifier,
    Array<{ widgetTrackingId: TrackingIdentifier; entry: OpenQuestionAnswerEntry }>
  >();
  try {
    const countStr = scormClient.get('cmi.interactions._count');
    const count = parseInt(countStr, 10) || 0;

    for (let i = 0; i < count; i++) {
      const type = scormClient.get(`cmi.interactions.${i}.type`);
      if (type !== 'fill-in') continue;

      const id = scormClient.get(`cmi.interactions.${i}.id`);
      const rawResponse = scormClient.get(`cmi.interactions.${i}.learner_response`);

      const decodedId = InteractionId.decode(id);
      if (!decodedId) continue;

      const [contentItemTrackingId, widgetTrackingId] = decodedId;
      const contentItemEntries = mutableMap.get(contentItemTrackingId) ?? [];

      contentItemEntries.push({ widgetTrackingId, entry: InteractionResponse.decode(rawResponse) });
      mutableMap.set(contentItemTrackingId, contentItemEntries);
    }

    const result: OpenQuestionAnswersForItems = Map<
      TrackingIdentifier,
      Map<TrackingIdentifier, OpenQuestionAnswerEntry>
    >(
      [...mutableMap].map(([contentItemTrackingId, entries]) => {
        const widgetMap = Map<TrackingIdentifier, OpenQuestionAnswerEntry>(
          entries.map(({ widgetTrackingId, entry }) => [widgetTrackingId, entry])
        );
        return [contentItemTrackingId, widgetMap];
      })
    );

    return result;
  } catch {
    return null;
  }
};
